import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { ACTIVE_FILTER_OPTIONS, BILLING_METRICS, WarehousingMasterType, billingChargeTypeLabel, billingChargeUnitLabel, companyOwnedWarehouses, masterDataMap, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingMasterData, WarehousingSearchFilters, WarehousingStorageRate } from '../warehousing.model';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingSearchPanelComponent } from '../warehousing-search-panel.component';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-billing-config',
  standalone: true,
  imports: [AuditDirectoryCellComponent,
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule, TableModule,
    TagModule, WarehousingPageHeaderComponent, WarehousingSearchPanelComponent
  ],
  templateUrl: './warehousing-billing-config.component.html'
})
export class WarehousingBillingConfigComponent implements OnInit {
  private fb = inject(FormBuilder);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  isLoading = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Storage Rates', 'Setup', '/warehousing/setup/storage-rates');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  rates: WarehousingStorageRate[] = [];
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  allWarehouses: WarehousingMasterData[] = [];
  warehouseOptions: WarehousingMasterData[] = [];
  masterData = masterDataMap();
  billingMetrics = BILLING_METRICS;
  activeFilterOptions = ACTIVE_FILTER_OPTIONS;

  searchForm = this.fb.group({
    keyword: [''],
    crmClientId: [null as number | null],
    crmAccountId: [null as number | null],
    regionId: [null as number | null],
    warehouseId: [null as number | null],
    metric: [null as string | null],
    activeStatus: [null as string | null]
  });

  ngOnInit(): void { this.loadConfig(); }

  loadConfig(): void {
    this.isLoading = true;
    const filters = this.searchFilters();
    forkJoin({
      rates: this.warehousingService.getStorageRates(filters),
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.rates = response.rates.data || [];
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.masterData.REGION = response.regions.data || [];
        this.allWarehouses = response.warehouses.data || [];
        this.warehouseOptions = companyOwnedWarehouses(this.allWarehouses);
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  applySearch(): void {
    this.loadConfig();
  }

  clearSearch(): void {
    this.searchForm.reset({
      keyword: '',
      crmClientId: null,
      crmAccountId: null,
      regionId: null,
      warehouseId: null,
      metric: null,
      activeStatus: null
    });
    this.loadConfig();
  }

  clientName(id?: number): string { return this.clients.find(client => client.id === id)?.companyName || '-'; }
  accountName(id?: number): string { return this.accounts.find(account => account.id === id)?.companyName || '-'; }
  filteredAccounts(clientId: number | null | undefined): CrmAccount[] {
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }
  masterName(type: WarehousingMasterType, id?: number): string {
    if (type === 'WAREHOUSE') {
      return this.allWarehouses.find(item => item.id === id)?.name || '-';
    }
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }
  statusSeverity = statusSeverity;
  chargeTypeLabel = billingChargeTypeLabel;
  chargeUnitLabel = billingChargeUnitLabel;

  private searchFilters(): WarehousingSearchFilters {
    return this.searchForm.getRawValue();
  }
}
